import React, { useEffect, useRef, useState } from "react";
import {
  List,
  Card,
  Descriptions,
  Divider,
  Button,
  Modal,
  Image,
  Grid,
  Typography,
  Row,
  Col,
} from "antd";
import ReviewOrder from "./ReviewOrder";
import ChatNowButton from "../common/ChatNowButton";

const { useBreakpoint } = Grid;
const { Text } = Typography;

const OrderCard = ({
  order,
  isSellerOrder,
  handleOrderStatusUpdate,
  fetchOrders,
  highlight,
  orderId,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const cardRef = useRef(null);
  const screens = useBreakpoint();

  useEffect(() => {
    // Scroll to the highlighted order if it matches the orderId
    if (highlight && order._id === orderId) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [highlight, order._id, orderId]);
  const user = isSellerOrder ? order.user : order.seller;

  const descriptionTitle = (
    <div style={{ marginBottom: "8px" }}>
      <div
        style={{
          fontSize: "18px",
          fontWeight: 600,
          color: "rgba(0, 0, 0, 0.88)",
        }}
      >
        Order ID
      </div>
      <Text
        type="secondary"
        style={{
          fontSize: "12px",
          fontWeight: "normal",
          display: "block",
          wordBreak: "break-all", // Forces long, unbroken IDs to wrap onto the next line
          whiteSpace: "normal", // Overrides default truncation
        }}
      >
        {order._id}
      </Text>
    </div>
  );

  return (
    <List.Item style={{ listStyleType: "none" }} ref={cardRef}>
      <Card
        style={{
          marginBottom: "20px",
          borderColor:
            highlight && order._id === orderId ? "#0000FF" : "#f0f0f0",
        }}
      >
        <Descriptions
          title={descriptionTitle}
          bordered
          layout={screens.xs ? "vertical" : "horizontal"}
          column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
        >
          <Descriptions.Item
            label={<strong>{isSellerOrder ? "Buyer" : "Seller"}</strong>}
          >
            {user.username}
          </Descriptions.Item>
          <Descriptions.Item label={<strong>Total Price</strong>}>
            {`$${Number(order.totalPrice).toFixed(2)}`}
          </Descriptions.Item>
          <Descriptions.Item label={<strong>Status</strong>}>
            {order.status}
          </Descriptions.Item>
          <Descriptions.Item label={<strong>Order Date</strong>}>
            {new Date(order.orderDate).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={<strong>Shipping Address</strong>}>
            {order.shippingAddress} {order.unitNumber}
          </Descriptions.Item>
        </Descriptions>
        <Divider />
        <List
          dataSource={order.items}
          renderItem={(item) => (
            <List.Item style={{ borderBottom: "none", padding: "8px 0" }}>
              <Card
                style={{ width: "100%" }}
                bodyStyle={{ padding: "12px 16px" }}
              >
                <Row
                  gutter={[16, 12]}
                  align="middle"
                  style={{ textAlign: screens.xs ? "center" : "left" }}
                >
                  {/* Image/Avatar: Full width on mobile (xs=24), fixed width on tablet/desktop */}
                  <Col xs={24} sm={4} md={3}>
                    <Image
                      src={`${item.product.images}?v=${item.product.imageVersion}`}
                      alt={item.product.name}
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                        borderRadius: "6px",
                      }}
                    />
                  </Col>

                  {/* Product Details: Full width on mobile, fills remaining space on tablet/desktop */}
                  <Col xs={24} sm={20} md={21}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "15px",
                        marginBottom: "4px",
                      }}
                    >
                      {item.product.name}
                    </div>
                    <Text type="secondary">
                      Quantity: {item.quantity} | Price: $
                      {Number(item.product.price).toFixed(2)}
                    </Text>
                  </Col>
                </Row>
              </Card>
            </List.Item>
          )}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginTop: "10px",
            flexWrap: "wrap", // Ensures buttons wrap cleanly on small screens
          }}
        >
          <ChatNowButton receiverId={user._id}></ChatNowButton>
          {!isSellerOrder && order.status === "To Rate" && (
            <Button type="primary" onClick={() => setOpenModal(true)}>
              Rate
            </Button>
          )}
          {((isSellerOrder && order.status === "To Ship") ||
            (!isSellerOrder && order.status === "To Receive")) && (
            <Button
              type="primary"
              onClick={() => handleOrderStatusUpdate(order._id)}
            >
              {isSellerOrder ? "Shipped" : "Received"}
            </Button>
          )}
        </div>
      </Card>

      <Modal
        open={openModal}
        footer={null}
        closable={false}
        centered={true}
        width={800}
      >
        <ReviewOrder
          setOpenModal={setOpenModal}
          order={order}
          fetchOrders={fetchOrders}
        />
      </Modal>
    </List.Item>
  );
};

export default OrderCard;
